import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPut } from "../api/client";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  ResponsiveContainer,
  CartesianGrid,
  ComposedChart,
  ScatterChart,
  Scatter,
  FunnelChart,
  Funnel,
  LabelList,
  Treemap,
  RadialBarChart,
  RadialBar,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ZAxis,
  Label,
} from "recharts";
import ChartColorPicker, { getChartColors, COLOR_PALETTES } from "./ChartColorPicker";
import "../styles/ChartWidget.css";

// General chart palette
const CHART_COLORS = [
  "#2563EB", // blue
  "#EF4444", // red
  "#16A34A", // green
  "#F97316", // orange
  "#6366F1", // indigo
  "#EC4899", // pink
];

// Separate palette for pie / doughnut (base)
const PIE_COLORS = CHART_COLORS;

// Custom tick component for rotated labels
const CustomXAxisTick = ({ x, y, payload, maxLength = 12 }) => {
  const text = String(payload.value || "");
  const displayText = text.length > maxLength 
    ? text.substring(0, maxLength - 2) + "..." 
    : text;
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={10}
        textAnchor="end"
        fill="#666"
        fontSize={11}
        transform="rotate(-45)"
      >
        {displayText}
      </text>
    </g>
  );
};

// Format axis label for display with common field name mappings
const formatAxisLabel = (fieldName) => {
  if (!fieldName) return "";
  
  const fieldMappings = {
    "count": "Count",
    "sum": "Sum",
    "avg": "Average",
    "value": "Value",
    "age_bucket": "Age Bucket",
    "agebucket": "Age Bucket",
    "age_days": "Age (Days)",
    "created": "Created Date",
    "updated": "Updated Date",
    "resolutiondate": "Resolution Date",
    "duedate": "Due Date",
    "issuetype": "Issue Type",
    "fixversions": "Fix Versions",
    "storypoints": "Story Points",
    "priority": "Priority",
    "status": "Status",
    "assignee": "Assignee",
    "reporter": "Reporter",
    "labels": "Labels",
    "components": "Components",
    "sprint": "Sprint",
    "severity": "Severity",
    "project": "Project",
    "resolution": "Resolution",
    "date": "Date",
  };
  
  const lowerField = fieldName.toLowerCase();
  if (fieldMappings[lowerField]) {
    return fieldMappings[lowerField];
  }
  
  return fieldName
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

export default function ChartWidget({
  widget,
  onRemove,
  onDrilldown,
  compact = false,
  onUpdateWidget,
}) {
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [showColorPicker, setShowColorPicker] = useState(false);

  const hasDataset = !!widget?.dataset_id;
  const xKey = widget?.x_field;
  const yKey = widget?.y_field;
  const groupKey = widget?.group_field;

  // Get custom colors from widget config
  const customColors = widget?.custom_colors || {};

  // ================================================================
  // FIX: Extract data labels for color picker
  // Always show INDIVIDUAL item labels (each slice, each bar, each point)
  // For stacked/grouped charts, show group values as separate color items
  // ================================================================
  const dataLabels = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const wType = widget?.widget_type;

    // For stacked charts: color each GROUP (stack segment), not each x-value
    if ((wType === "stacked_bar" || wType === "stacked_area") && groupKey) {
      const groups = [...new Set(data.map(d => d[groupKey]).filter(Boolean))];
      return groups.map(String);
    }

    // For combo charts: color each series (y_field and group_field)
    if (wType === "combo" && yKey && groupKey) {
      return [formatAxisLabel(yKey), formatAxisLabel(groupKey)];
    }

    // For line/area (single series): only one color needed
    if ((wType === "line" || wType === "area") && !groupKey) {
      return [formatAxisLabel(yKey) || "Series"];
    }

    // For KPI / gauge: single color
    if (wType === "kpi" || wType === "gauge") {
      return ["value"];
    }

    // For everything else (bar, column, pie, doughnut, funnel, treemap, waterfall, radar, scatter):
    // Show INDIVIDUAL data point labels from the X field — each bar/slice gets its own color
    const xField = xKey || Object.keys(data[0])[0];
    const labels = data.map(d => d[xField]).filter(v => v != null);
    return [...new Set(labels)].map(String);
  }, [data, xKey, yKey, groupKey, widget?.widget_type]);

  // Effective color palette
  const effectiveColors = useMemo(() => {
    if (Object.keys(customColors).length > 0 && dataLabels.length > 0) {
      return dataLabels.map((label, idx) => 
        customColors[label] || CHART_COLORS[idx % CHART_COLORS.length]
      );
    }
    return widget?.color
      ? [widget.color, ...CHART_COLORS.filter((c) => c !== widget.color)]
      : CHART_COLORS;
  }, [widget?.color, customColors, dataLabels]);

  const pieColors = useMemo(() => {
    if (Object.keys(customColors).length > 0 && dataLabels.length > 0) {
      return dataLabels.map((label, idx) => 
        customColors[label] || PIE_COLORS[idx % PIE_COLORS.length]
      );
    }
    return widget?.color
      ? [widget.color, ...PIE_COLORS.filter((c) => c !== widget.color)]
      : PIE_COLORS;
  }, [widget?.color, customColors, dataLabels]);

  // Get color for a specific data item by name or index
  const getColorForItem = (itemName, index) => {
    const key = String(itemName);
    if (customColors[key]) {
      return customColors[key];
    }
    return effectiveColors[index % effectiveColors.length];
  };

  // Handle color save from picker
  const handleColorSave = (newColors) => {
    setShowColorPicker(false);
    if (onUpdateWidget) {
      onUpdateWidget(widget.id, { custom_colors: newColors });
    } else {
      // Fallback: try to save directly via API
      try {
        apiPut(`/widgets/${widget.id}`, { custom_colors: newColors });
      } catch (err) {
        console.error("Failed to save colors:", err);
      }
    }
  };

  const withAlpha = (hex, alphaHex) => {
    if (!hex || typeof hex !== "string") return hex;
    if (hex.startsWith("#") && hex.length === 7) {
      return `${hex}${alphaHex}`;
    }
    return hex;
  };

  // Load data whenever dataset changes
  useEffect(() => {
    if (!hasDataset) {
      setData([]);
      setError("");
      return;
    }

    setError("");
    apiGet(`/query/dataset/${widget.dataset_id}`)
      .then((res) => setData(res.data || []))
      .catch((err) => setError(String(err)));
  }, [widget?.dataset_id, hasDataset]);

  const columns = useMemo(
    () => (data.length ? Object.keys(data[0]) : []),
    [data]
  );

  const title =
    widget?.title || widget?.widget_type?.toUpperCase?.() || "Widget";

  // Auto-detect the correct axis field names from data
  const { effectiveXKey, effectiveYKey } = useMemo(() => {
    if (!data.length || !columns.length) {
      return { effectiveXKey: xKey, effectiveYKey: yKey };
    }
    
    const valueFields = ["count", "sum", "avg", "value", "total"];
    const categoryFields = [
      "age_bucket", "agebucket", "status", "priority", "assignee", 
      "labels", "sprint", "issuetype", "project", "severity",
      "components", "resolution", "reporter", "date", "created",
      "updated", "name", "label", "category", "group"
    ];
    
    let detectedXKey = xKey;
    let detectedYKey = yKey;
    
    if (columns.length === 2) {
      const col1Lower = columns[0].toLowerCase();
      const col2Lower = columns[1].toLowerCase();
      
      const col1IsValue = valueFields.includes(col1Lower);
      const col2IsValue = valueFields.includes(col2Lower);
      
      if (col1IsValue && !col2IsValue) {
        detectedYKey = columns[0];
        detectedXKey = columns[1];
      } else if (col2IsValue && !col1IsValue) {
        detectedYKey = columns[1];
        detectedXKey = columns[0];
      } else {
        detectedXKey = xKey || columns[0];
        detectedYKey = yKey || columns[1];
      }
    }
    
    if (detectedXKey && !columns.some(c => c.toLowerCase() === detectedXKey.toLowerCase())) {
      const match = columns.find(c => categoryFields.includes(c.toLowerCase()));
      if (match) detectedXKey = match;
    }
    
    if (detectedYKey && !columns.some(c => c.toLowerCase() === detectedYKey.toLowerCase())) {
      const match = columns.find(c => valueFields.includes(c.toLowerCase()));
      if (match) detectedYKey = match;
    }
    
    return { 
      effectiveXKey: detectedXKey || xKey, 
      effectiveYKey: detectedYKey || yKey 
    };
  }, [data, columns, xKey, yKey]);

  const xAxisLabel = formatAxisLabel(effectiveXKey);
  const yAxisLabel = formatAxisLabel(effectiveYKey);

  const needsRotatedLabels = useMemo(() => {
    if (!data.length || !xKey) return false;
    if (data.length > 6) return true;
    const hasLongLabels = data.some(row => {
      const label = String(row[xKey] || "");
      return label.length > 10;
    });
    return hasLongLabels;
  }, [data, xKey]);

  const chartMargin = useMemo(() => {
    if (needsRotatedLabels) {
      return { top: 10, right: 20, left: 20, bottom: 80 };
    }
    return { top: 10, right: 20, left: 20, bottom: 40 };
  }, [needsRotatedLabels]);

  // Stacked data for stacked_bar / stacked_area
  const stackedConfig = useMemo(() => {
    if (!data.length || !xKey || !yKey || !groupKey) return null;

    const groupsSet = new Set();
    const byX = new Map();

    data.forEach((row) => {
      const xv = row[xKey];
      const gv = row[groupKey];
      if (xv == null || gv == null) return;

      const xStr = String(xv);
      const gStr = String(gv);
      groupsSet.add(gStr);

      const existing = byX.get(xStr) || { [xKey]: xv };
      existing[gStr] = Number(row[yKey] ?? 0);
      byX.set(xStr, existing);
    });

    const groups = Array.from(groupsSet);
    const rows = Array.from(byX.values());
    if (!groups.length || !rows.length) return null;
    return { groups, rows };
  }, [data, xKey, yKey, groupKey]);

  // Filter-aware click handler
  const handleDataPointClick = (payload) => {
    if (!onDrilldown) return;
    const filter = {};

    if (payload && xKey && payload[xKey] !== undefined) {
      filter[xKey] = payload[xKey];
    }
    if (payload && groupKey && payload[groupKey] !== undefined) {
      filter[groupKey] = payload[groupKey];
    }

    if (!Object.keys(filter).length) {
      onDrilldown();
    } else {
      onDrilldown(filter);
    }
  };

  // Common axis props
  const getXAxisProps = () => ({
    dataKey: xKey,
    tick: needsRotatedLabels ? <CustomXAxisTick maxLength={15} /> : { fontSize: 11 },
    interval: 0,
    height: needsRotatedLabels ? 70 : 30,
  });

  const getYAxisProps = () => ({
    tick: { fontSize: 11 },
    width: 60,
  });

  // Check if chart type supports color customization
  const supportsColors = !["table"].includes(widget?.widget_type);

  let body;

  if (error) {
    body = (
      <div style={{ padding: 8, color: "#b91c1c", fontSize: 12 }}>
        {error}
      </div>
    );
  } else if (!hasDataset) {
    body = (
      <div style={{ padding: 8, fontSize: 12, color: "#6b7280" }}>
        Select a dataset to see data.
      </div>
    );
  } else {
    // ---------- BAR / COLUMN ----------
    if (
      (widget.widget_type === "bar" ||
        widget.widget_type === "column") &&
      xKey &&
      yKey
    ) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart data={data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...getXAxisProps()}>
              <Label 
                value={xAxisLabel} 
                position="bottom" 
                offset={needsRotatedLabels ? 60 : 20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis {...getYAxisProps()}>
              <Label 
                value={yAxisLabel} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip 
              formatter={(value, name) => [value, yAxisLabel]}
              labelFormatter={(label) => `${xAxisLabel}: ${label}`}
            />
            <Bar
              dataKey={yKey}
              name={yAxisLabel}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`}
                  fill={getColorForItem(entry[xKey], index)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    // ---------- LINE ----------
    else if (widget.widget_type === "line" && xKey && yKey) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <LineChart data={data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...getXAxisProps()}>
              <Label 
                value={xAxisLabel} 
                position="bottom" 
                offset={needsRotatedLabels ? 60 : 20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis {...getYAxisProps()}>
              <Label 
                value={yAxisLabel} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip 
              formatter={(value, name) => [value, yAxisLabel]}
              labelFormatter={(label) => `${xAxisLabel}: ${label}`}
            />
            <Line
              type="monotone"
              dataKey={yKey}
              stroke={customColors[formatAxisLabel(yKey)] || customColors["Series"] || effectiveColors[0]}
              name={yAxisLabel}
              dot={{ r: 4, fill: customColors[formatAxisLabel(yKey)] || customColors["Series"] || effectiveColors[0] }}
              activeDot={{ r: 6 }}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }
    // ---------- AREA ----------
    else if (widget.widget_type === "area" && xKey && yKey) {
      const areaColor = customColors[formatAxisLabel(yKey)] || customColors["Series"] || effectiveColors[0];
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <AreaChart data={data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...getXAxisProps()}>
              <Label 
                value={xAxisLabel} 
                position="bottom" 
                offset={needsRotatedLabels ? 60 : 20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis {...getYAxisProps()}>
              <Label 
                value={yAxisLabel} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip 
              formatter={(value, name) => [value, yAxisLabel]}
              labelFormatter={(label) => `${xAxisLabel}: ${label}`}
            />
            <Area
              type="monotone"
              dataKey={yKey}
              stroke={areaColor}
              fill={withAlpha(areaColor, "33")}
              name={yAxisLabel}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            />
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    // ---------- PIE ----------
    else if (widget.widget_type === "pie" && xKey && yKey) {
      const getFieldValue = (row, fieldName) => {
        if (row[fieldName] !== undefined) return row[fieldName];
        const lowerField = fieldName.toLowerCase();
        for (const key of Object.keys(row)) {
          if (key.toLowerCase() === lowerField) return row[key];
        }
        return undefined;
      };
      
      const pieData = data.map((row, index) => {
        const nameValue = getFieldValue(row, xKey);
        const valueNum = getFieldValue(row, yKey);
        return {
          ...row,
          name: nameValue !== undefined && nameValue !== null ? String(nameValue) : `Item ${index + 1}`,
          value: Number(valueNum || 0),
        };
      });
      
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              outerRadius="80%"
              label={({ name, percent }) => 
                `${String(name).length > 10 ? String(name).substring(0, 8) + '...' : name} (${(percent * 100).toFixed(0)}%)`
              }
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`slice-${index}`}
                  fill={getColorForItem(entry.name, index)}
                  onClick={() => handleDataPointClick(data[index])}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => [value, formatAxisLabel(yKey)]}
              labelFormatter={(label) => String(label)}
            />
            <Legend 
              formatter={(value) => {
                const str = String(value);
                return str.length > 15 ? str.substring(0, 12) + '...' : str;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    // ---------- DOUGHNUT ----------
    else if (widget.widget_type === "doughnut" && xKey && yKey) {
      const getFieldValueDoughnut = (row, fieldName) => {
        if (row[fieldName] !== undefined) return row[fieldName];
        const lowerField = fieldName.toLowerCase();
        for (const key of Object.keys(row)) {
          if (key.toLowerCase() === lowerField) return row[key];
        }
        return undefined;
      };
      
      const doughnutData = data.map((row, index) => {
        const nameValue = getFieldValueDoughnut(row, xKey);
        const valueNum = getFieldValueDoughnut(row, yKey);
        return {
          ...row,
          name: nameValue !== undefined && nameValue !== null ? String(nameValue) : `Item ${index + 1}`,
          value: Number(valueNum || 0),
        };
      });
      
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <PieChart>
            <Pie
              data={doughnutData}
              dataKey="value"
              nameKey="name"
              innerRadius="50%"
              outerRadius="80%"
              label={({ name, percent }) => 
                `${String(name).length > 10 ? String(name).substring(0, 8) + '...' : name} (${(percent * 100).toFixed(0)}%)`
              }
            >
              {doughnutData.map((entry, index) => (
                <Cell
                  key={`slice-${index}`}
                  fill={getColorForItem(entry.name, index)}
                  onClick={() => handleDataPointClick(data[index])}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => [value, formatAxisLabel(yKey)]}
              labelFormatter={(label) => String(label)}
            />
            <Legend 
              formatter={(value) => {
                const str = String(value);
                return str.length > 15 ? str.substring(0, 12) + '...' : str;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    }
    // ---------- COMBO ----------
    else if (widget.widget_type === "combo" && xKey && yKey && groupKey) {
      const barColor = customColors[formatAxisLabel(yKey)] || effectiveColors[0];
      const lineColor = customColors[formatAxisLabel(groupKey)] || effectiveColors[1 % effectiveColors.length];
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <ComposedChart data={data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...getXAxisProps()}>
              <Label 
                value={formatAxisLabel(xKey)} 
                position="bottom" 
                offset={needsRotatedLabels ? 60 : 20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis {...getYAxisProps()} />
            <Tooltip 
              formatter={(value, name) => [value, formatAxisLabel(name)]}
              labelFormatter={(label) => `${formatAxisLabel(xKey)}: ${label}`}
            />
            <Legend />
            <Bar
              dataKey={yKey}
              fill={barColor}
              name={formatAxisLabel(yKey)}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            />
            <Line
              type="monotone"
              dataKey={groupKey}
              stroke={lineColor}
              name={formatAxisLabel(groupKey)}
              dot={false}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }
    // ---------- SCATTER ----------
    else if (widget.widget_type === "scatter" && xKey && yKey) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <ScatterChart margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }}>
              <Label 
                value={formatAxisLabel(xKey)} 
                position="bottom" 
                offset={20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis dataKey={yKey} tick={{ fontSize: 11 }}>
              <Label 
                value={formatAxisLabel(yKey)} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip 
              formatter={(value, name) => [value, formatAxisLabel(name)]}
            />
            <Legend />
            <Scatter
              data={data}
              fill={effectiveColors[0]}
              name={`${formatAxisLabel(xKey)} vs ${formatAxisLabel(yKey)}`}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }
    // ---------- RADAR ----------
    else if (widget.widget_type === "radar" && xKey && yKey) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <RadarChart data={data}>
            <PolarGrid />
            <PolarAngleAxis 
              dataKey={xKey} 
              tick={{ fontSize: 10 }}
              tickFormatter={(value) => value.length > 10 ? value.substring(0, 8) + '...' : value}
            />
            <PolarRadiusAxis />
            <Tooltip />
            <Legend />
            <Radar
              name={formatAxisLabel(yKey)}
              dataKey={yKey}
              stroke={effectiveColors[0]}
              fill={withAlpha(effectiveColors[0], "55")}
              fillOpacity={0.7}
              onClick={(entry) =>
                handleDataPointClick(entry && entry.payload)
              }
            />
          </RadarChart>
        </ResponsiveContainer>
      );
    }
    // ---------- BUBBLE ----------
    else if (widget.widget_type === "bubble" && xKey && yKey) {
      const hasSize = !!groupKey;
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <ScatterChart margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }}>
              <Label 
                value={formatAxisLabel(xKey)} 
                position="bottom" 
                offset={20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis dataKey={yKey} tick={{ fontSize: 11 }}>
              <Label 
                value={formatAxisLabel(yKey)} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            {hasSize && (
              <ZAxis
                dataKey={groupKey}
                type="number"
                range={[60, 400]}
                name={formatAxisLabel(groupKey)}
              />
            )}
            <Tooltip />
            <Legend />
            <Scatter
              data={data}
              fill={effectiveColors[0]}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            />
          </ScatterChart>
        </ResponsiveContainer>
      );
    }
    // ---------- KPI ----------
    else if (widget.widget_type === "kpi" && yKey) {
      const getKpiValue = (row, fieldName) => {
        if (!row) return "-";
        if (row[fieldName] !== undefined) return row[fieldName];
        const lowerField = fieldName.toLowerCase();
        for (const key of Object.keys(row)) {
          if (key.toLowerCase() === lowerField) return row[key];
        }
        return "-";
      };
      
      const kpiValue = data.length ? getKpiValue(data[0], yKey) : "-";
      const displayValue = kpiValue !== null && kpiValue !== undefined ? kpiValue : "-";
      
      const valueStr = String(displayValue);
      let fontSize = 48;
      if (valueStr.length >= 5) fontSize = 32;
      else if (valueStr.length >= 4) fontSize = 36;
      else if (valueStr.length >= 3) fontSize = 42;
      
      const kpiColor = customColors["value"] || effectiveColors[0];
      
      body = (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              fontSize: fontSize,
              fontWeight: 800,
              color: kpiColor,
              textShadow: "0 3px 8px rgba(0, 0, 0, 0.25)",
              transform: "perspective(500px) translateZ(20px)",
              transition: "all 0.3s ease",
              lineHeight: 1.1,
            }}
          >
            {displayValue}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginTop: 8,
            }}
          >
            {formatAxisLabel(yKey)}
          </div>
        </div>
      );
    }
    // ---------- TABLE ----------
    else if (widget.widget_type === "table") {
      body = (
        <div
          style={{
            height: "100%",
            overflow: "auto",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background:
              "linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)",
            transform: "perspective(500px) translateZ(10px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {data.length === 0 ? (
            <div style={{ padding: 8, fontSize: 12, color: "#6b7280" }}>
              No data for this dataset.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: "#e5e7eb" }}>
                  {columns.map((col) => (
                    <th
                      key={col}
                      style={{
                        padding: "4px 6px",
                        textAlign: "left",
                        borderBottom: "1px solid #d1d5db",
                        position: "sticky",
                        top: 0,
                        background: "#e5e7eb",
                        zIndex: 1,
                      }}
                    >
                      {formatAxisLabel(col)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      borderBottom: "1px solid #e5e7eb",
                      background:
                        i % 2 === 0 ? "#ffffff" : "#f9fafb",
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col}
                        style={{
                          padding: "4px 6px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {String(row[col] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    // ---------- FUNNEL ----------
    else if (widget.widget_type === "funnel" && xKey && yKey) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <FunnelChart>
            <Tooltip />
            <Funnel
              dataKey={yKey}
              nameKey={xKey}
              data={data}
              onClick={(entry) => handleDataPointClick(entry?.payload)}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorForItem(entry[xKey], index)}
                />
              ))}
              <LabelList position="right" dataKey={xKey} />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      );
    }
    // ---------- TREEMAP ----------
    else if (widget.widget_type === "treemap" && xKey && yKey) {
      const treemapData = data.map((row) => ({
        name: String(row[xKey]),
        size: Number(row[yKey] ?? 0),
        __raw: row,
      }));
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <Treemap
            data={treemapData}
            dataKey="size"
            nameKey="name"
            aspectRatio={4 / 3}
            stroke="#fff"
            contentStyle={{ cursor: "pointer" }}
            onClick={(entry) =>
              handleDataPointClick(entry?.__raw || entry)
            }
          />
        </ResponsiveContainer>
      );
    }
    // ---------- WATERFALL ----------
    else if (widget.widget_type === "waterfall" && xKey && yKey) {
      let running = 0;
      const wfData = data.map((row) => {
        const v = Number(row[yKey] ?? 0);
        const start = running;
        running += v;
        return { label: row[xKey], start, value: v, __raw: row };
      });

      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart data={wfData} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="label" 
              tick={needsRotatedLabels ? <CustomXAxisTick maxLength={15} /> : { fontSize: 11 }}
              interval={0}
              height={needsRotatedLabels ? 70 : 30}
            />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="start" stackId="a" fill="transparent" />
            <Bar
              dataKey="value"
              stackId="a"
              name={formatAxisLabel(yKey)}
              onClick={(entry) =>
                handleDataPointClick(entry?.payload?.__raw)
              }
            >
              {wfData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorForItem(entry.label, index)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    }
    // ---------- GAUGE ----------
    else if (widget.widget_type === "gauge" && yKey) {
      const getGaugeValue = (row, fieldName) => {
        if (!row) return 0;
        if (row[fieldName] !== undefined) return Number(row[fieldName] || 0);
        const lowerField = fieldName.toLowerCase();
        for (const key of Object.keys(row)) {
          if (key.toLowerCase() === lowerField) return Number(row[key] || 0);
        }
        return 0;
      };
      
      const gaugeValue = data.length ? getGaugeValue(data[0], yKey) : 0;
      const gaugeColor = customColors["value"] || effectiveColors[0];

      body = (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              fontSize: 48,
              fontWeight: 800,
              color: gaugeColor,
              textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              zIndex: 2,
            }}
          >
            {gaugeValue}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginTop: 4,
            }}
          >
            {formatAxisLabel(yKey)}
          </div>
        </div>
      );
    }
    // ---------- STACKED BAR ----------
    else if (
      widget.widget_type === "stacked_bar" &&
      stackedConfig &&
      xKey
    ) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <BarChart data={stackedConfig.rows} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...getXAxisProps()}>
              <Label 
                value={formatAxisLabel(xKey)} 
                position="bottom" 
                offset={needsRotatedLabels ? 60 : 20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis {...getYAxisProps()}>
              <Label 
                value={formatAxisLabel(yKey)} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip />
            <Legend />
            {stackedConfig.groups.map((g, idx) => (
              <Bar
                key={g}
                dataKey={g}
                stackId="a"
                fill={getColorForItem(g, idx)}
                onClick={(entry) => {
                  const payload = entry?.payload || {};
                  const patched = { ...payload, [groupKey]: g };
                  handleDataPointClick(patched);
                }}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );
    }
    // ---------- STACKED AREA ----------
    else if (
      widget.widget_type === "stacked_area" &&
      stackedConfig &&
      xKey
    ) {
      body = (
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <AreaChart data={stackedConfig.rows} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis {...getXAxisProps()}>
              <Label 
                value={formatAxisLabel(xKey)} 
                position="bottom" 
                offset={needsRotatedLabels ? 60 : 20}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151' }}
              />
            </XAxis>
            <YAxis {...getYAxisProps()}>
              <Label 
                value={formatAxisLabel(yKey)} 
                angle={-90} 
                position="insideLeft" 
                offset={10}
                style={{ fontSize: 12, fontWeight: 600, fill: '#374151', textAnchor: 'middle' }}
              />
            </YAxis>
            <Tooltip />
            <Legend />
            {stackedConfig.groups.map((g, idx) => {
              const baseColor = getColorForItem(g, idx);
              return (
                <Area
                  key={g}
                  type="monotone"
                  dataKey={g}
                  stackId="a"
                  stroke={baseColor}
                  fill={withAlpha(baseColor, "40")}
                  onClick={(entry) => {
                    const payload = entry?.payload || {};
                    const patched = { ...payload, [groupKey]: g };
                    handleDataPointClick(patched);
                  }}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    // ---------- FALLBACK ----------
    else {
      body = (
        <div style={{ padding: 8, fontSize: 12, color: "#6b7280" }}>
          Configure this widget (fields / type) to see data.
        </div>
      );
    }
  }

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onRemove) onRemove();
  };

  const handleDrilldownIconClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDrilldown) {
      onDrilldown();
    }
  };

  const handleColorPickerClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowColorPicker(true);
  };

  return (
    <>
      <div
        className={`chart-widget-card ${
          compact ? "chart-widget-card--compact" : ""
        }`}
        style={{
          height: "100%",
          width: "100%",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          borderRadius: 14,
          background:
            "linear-gradient(145deg, #ffffff 0%, #f3f4ff 45%, #e5e7ff 100%)",
          border: "1px solid rgba(148, 163, 184, 0.6)",
          boxShadow: `
            0 10px 25px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            0 0 20px rgba(59, 130, 246, 0.15)
          `,
          transform: "perspective(1000px) rotateX(2deg) rotateY(1deg)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform =
            "perspective(1000px) rotateX(1deg) rotateY(2deg) translateY(-4px)";
          e.currentTarget.style.boxShadow = `
            0 20px 40px -5px rgba(0, 0, 0, 0.2),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            inset 0 1px 0 rgba(255, 255, 255, 0.9),
            0 0 30px rgba(59, 130, 246, 0.25)
          `;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform =
            "perspective(1000px) rotateX(2deg) rotateY(1deg)";
          e.currentTarget.style.boxShadow = `
            0 10px 25px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.8),
            0 0 20px rgba(59, 130, 246, 0.15)
          `;
        }}
      >
        {/* Subtle 3D border effect */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: 14,
            border: "1px solid rgba(255, 255, 255, 0.6)",
            pointerEvents: "none",
          }}
        />

        {/* Header */}
        <div
          style={{
            fontWeight: 600,
            fontSize: 14,
            marginBottom: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#0f172a",
            position: "relative",
            zIndex: 2,
          }}
        >
          <span>{title}</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {data.length > 0 && (
              <span style={{ fontSize: 11, color: "#9ca3af" }}>
                {data.length} rows
              </span>
            )}
            {/* Color Picker Button */}
            {supportsColors && hasDataset && data.length > 0 && (
              <button
                type="button"
                className="widget-icon-btn"
                onClick={handleColorPickerClick}
                title="Customize colors"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  padding: "4px",
                  borderRadius: "4px",
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(0,0,0,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                🎨
              </button>
            )}
            {onDrilldown && hasDataset && (
              <button
                type="button"
                className="widget-icon-btn"
                onClick={handleDrilldownIconClick}
                title="Drilldown / View data"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  padding: "4px",
                  borderRadius: "4px",
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(0,0,0,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                🔍
              </button>
            )}
            {onRemove && (
              <button
                type="button"
                className="widget-remove-btn"
                onClick={handleRemoveClick}
                title="Remove widget"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  padding: "4px",
                  borderRadius: "4px",
                  transition: "background-color 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  height: "24px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor =
                    "rgba(239,68,68,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "transparent")
                }
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            minHeight: "200px",
            position: "relative",
            zIndex: 1,
          }}
        >
          {body}
        </div>
      </div>

      {/* Color Picker Modal */}
      <ChartColorPicker
        isOpen={showColorPicker}
        onClose={() => setShowColorPicker(false)}
        chartType={widget?.widget_type}
        dataLabels={dataLabels}
        currentColors={customColors}
        onSave={handleColorSave}
      />
    </>
  );
}